import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../shared/auth/auth.service';
import { UserService } from '../shared/auth/user.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

// Import User class from AuthService to ensure type compatibility
import { User } from '../shared/auth/auth.service';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss']
})
export class LayoutComponent implements OnInit, OnDestroy {
  currentYear = new Date().getFullYear();
  isLoggedIn = false;
  user: User | null = null;

  // Dropdown states
  isDropdownOpen = false;
  isLanguageDropdownOpen = false;

  // Subscriptions to manage
  private authSubscription: Subscription | null = null;
  private userSubscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;

  constructor(
    public translate: TranslateService,
    private authService: AuthService,
    private userService: UserService,
    private router: Router
  ) {
    // Set language
    var language = localStorage.getItem("language");
    translate.use(language && language.match(/en|ro/) ? language : 'en');
    localStorage.setItem("language", language && language.match(/en|ro/) ? language : 'en');
  }

  ngOnInit(): void {
    // Subscribe to authentication state changes
    this.authSubscription = this.authService.authState$.subscribe(
      (authenticated) => {
        this.isLoggedIn = authenticated;
        if (authenticated) {
          this.loadUserProfile();
        } else {
          this.user = null;
        }
      }
    );

    // Subscribe to user changes
    this.userSubscription = this.authService.currentUserChanged.subscribe(
      (user) => {
        this.user = user;
      }
    );

    // Subscribe to router events to check auth on navigation
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkAuthStatus();
    });

    // Initial auth check
    this.checkAuthStatus();
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    this.authSubscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  // Check initial authentication status
  private checkAuthStatus() {
    this.authService.isLoggedIn().then(
      (authenticated) => {
        this.isLoggedIn = authenticated;
        if (this.isLoggedIn) {
          this.loadUserProfile();
        }
      }
    ).catch((error) => {
      this.isLoggedIn = false;
      this.user = null;
    });
  }

  // Load user profile
  private loadUserProfile() {
    // First, check if we have a valid user from AuthService
    const currentUser = this.authService.currentUser;

    // Check if current user is valid
    const isValidUser = currentUser &&
      currentUser.id &&
      currentUser.username;

    if (isValidUser) {
      this.user = currentUser;
      this.isLoggedIn = true;
      return;
    }

    // Fallback to fetching user profile from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);

        // Create a new User instance
        const userWithRole = new User();
        userWithRole.id = parsedUser.id ? parsedUser.id.toString() : '';
        userWithRole.username = parsedUser.username || '';
        userWithRole.email = parsedUser.email || '';
        userWithRole.role = parsedUser.role || '';
        userWithRole.name = parsedUser.name || '';

        this.user = userWithRole;
        this.isLoggedIn = true;

        // Update AuthService's current user
        this.authService.currentUser = userWithRole;

        return;
      } catch (error) {
        console.error('Error parsing stored user', error);
      }
    }

    // Final fallback: fetch user profile from service
    this.userService.get().subscribe(
      (user: any) => {

        // Create a new User instance with required properties
        const userWithRole = new User();
        userWithRole.id = user.id ? user.id.toString() :
          (user._id ? user._id.toString() : '');
        userWithRole.username = user.username || '';
        userWithRole.email = user.email || '';
        userWithRole.role = user.role || '';
        userWithRole.name = user.name || '';

        this.user = userWithRole;
        this.isLoggedIn = true;

        // Update AuthService's current user and localStorage
        this.authService.currentUser = userWithRole;
        localStorage.setItem('user', JSON.stringify(userWithRole));

      },
      (error) => {
        console.error('Failed to load user profile', error);
        // Logout if profile fetch fails
        this.logout();
      }
    );
  }

  // Logout method
  logout() {
    this.authService.logout();
    this.isLoggedIn = false;
    this.user = null;
    this.router.navigate(['/login']);
  }

  // Change language method
  onChangeLanguage(language: string) {
    localStorage.setItem("language", language);
    this.translate.use(language);
    this.isLanguageDropdownOpen = false; // Close dropdown after selection
  }

  // Navigate to profile page
  navigateToProfile() {
    this.router.navigate(['/profile']);
  }

  // Toggle dropdown method for user profile
  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
    this.isLanguageDropdownOpen = false; // Close language dropdown

    // Close dropdown when clicking outside
    if (this.isDropdownOpen) {
      const closeDropdown = (e: MouseEvent) => {
        this.isDropdownOpen = false;
        document.removeEventListener('click', closeDropdown);
      };

      // Slight delay to prevent immediate closure
      setTimeout(() => {
        document.addEventListener('click', closeDropdown);
      });
    }
  }

  // Toggle dropdown method for language
  toggleLanguageDropdown(event: Event) {
    event.stopPropagation();
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
    this.isDropdownOpen = false; // Close user profile dropdown

    // Close dropdown when clicking outside
    if (this.isLanguageDropdownOpen) {
      const closeDropdown = (e: MouseEvent) => {
        this.isLanguageDropdownOpen = false;
        document.removeEventListener('click', closeDropdown);
      };

      // Slight delay to prevent immediate closure
      setTimeout(() => {
        document.addEventListener('click', closeDropdown);
      });
    }
  }
}
